import {
  buscarFechaCaducidad,
  acumularProducto,
  esRuido,
  mencionaCaducidad,
  esTokenNumerico,
  aNumero,
  cuadraImporte,
  contarLetras,
  UNIDADES,
} from "./comun";

/**
 * Parser de respaldo para facturas de proveedor en PDF, sin maqueta conocida.
 *
 * En el espíritu de Cotizacion.js: no depende del RFC ni de las columnas del proveedor,
 * sino de señales. La señal fuerte de una partida con control de lotes es la CADUCIDAD —
 * una fecha plausible dentro de una ventana de años — con un token de lote pegado a ella.
 * La cantidad se valida con la aritmética del renglón (cantidad x precio ~= importe).
 *
 * Sólo se usa cuando el parser específico del proveedor no encontró nada: hoy ese caso
 * termina en el modal de "no se encontraron coincidencias", así que en el peor escenario
 * deja lo mismo que antes. Lo que proponga pasa igual por la revisión del usuario.
 */

// Un lote trae al menos un dígito y no es una clave SAT de 8 dígitos ni un importe
const LOTE = /^(?=.*\d)[A-Za-z0-9][A-Za-z0-9\-/.]{1,19}$/;
const CLAVE_SAT = /^\d{8}$/;
// Columna "Objeto de impuesto" del CFDI 4.0: aparece en la fila de muchos proveedores
const OBJ_IMPUESTO = /(?:^|\s)(?:S[ií]|No)\s+objeto\s+de(?:\s+impuesto)?\s*/i;
// Cuántas filas puede sobrevivir una partida esperando su caducidad
const VIGENCIA_PENDIENTE = 2;

const esLote = (token) => !!token && LOTE.test(token) && !CLAVE_SAT.test(token);

/** Quita del inicio los números de partida, códigos y claves que preceden al texto. */
function limpiarDescripcion(tokens) {
  const limpios = [...tokens];
  while (limpios.length > 1) {
    const primero = limpios[0];
    const esCodigo = /^[\d.,/-]+$/.test(primero) || CLAVE_SAT.test(primero);
    const esUnidad = UNIDADES.test(primero) || /^[A-Z]\d{2}$/.test(primero);
    if (!esCodigo && !esUnidad) break;
    limpios.shift();
  }
  while (limpios.length > 1 && esTokenNumerico(limpios[limpios.length - 1])) limpios.pop();
  return limpios.join(" ").replace(OBJ_IMPUESTO, " ").replace(/\s+/g, " ").trim();
}

/**
 * Cantidad de la fila, identificada por aritmética: cantidad x precio ~= importe.
 * Devuelve también en qué token quedó, porque el lote es el que va pegado a su izquierda
 * (buscar el índice por valor tomaría el primer número igual, que puede estar dentro de la
 * descripción: "SOLUCION 5 ML ... 5.000 PZA").
 */
function cantidadDeLaFila(tokens) {
  const numericos = [];
  tokens.forEach((token, indice) => {
    if (esTokenNumerico(token)) numericos.push({ indice, valor: aNumero(token) });
  });
  if (numericos.length < 3) return null;

  const importe = numericos[numericos.length - 1];
  const precio = numericos[numericos.length - 2];
  for (let i = numericos.length - 3; i >= 0; i--) {
    if (cuadraImporte(numericos[i].valor, precio.valor, importe.valor)) return numericos[i];
  }
  return null;
}

/**
 * Fila que trae la caducidad: el lote es el token pegado a la fecha (antes, si no después)
 * y la descripción es el tramo con más letras de los dos lados.
 */
function analizarFilaConFecha(linea, fecha) {
  const antes = linea.slice(0, fecha.indice).split(" ").filter(Boolean);
  const despues = linea.slice(fecha.indice + fecha.texto.length).split(" ").filter(Boolean);

  let lote = null;
  let tramoAntes = antes;
  let tramoDespues = despues;

  if (esLote(antes[antes.length - 1])) {
    lote = antes[antes.length - 1];
    tramoAntes = antes.slice(0, -1);
  } else if (esLote(despues[0])) {
    lote = despues[0];
    tramoDespues = despues.slice(1);
  }
  if (!lote) return null;

  const descAntes = limpiarDescripcion(tramoAntes);
  const descDespues = limpiarDescripcion(tramoDespues);
  const descripcion = contarLetras(descDespues) > contarLetras(descAntes) ? descDespues : descAntes;
  if (contarLetras(descripcion) < 6) return null;

  const cantidad = cantidadDeLaFila([...antes, ...despues]);
  return { descripcion, lote, cantidad: cantidad ? cantidad.valor : null };
}

/**
 * Fila que parece partida pero sin caducidad (queda en el renglón de continuación, como
 * hace PISA). Se exige la aritmética completa del renglón para no abrir la puerta a
 * cualquier línea con números.
 */
function analizarFilaSinFecha(linea) {
  const tokens = linea.split(" ").filter(Boolean);
  if (tokens.length < 5) return null;
  if (!esTokenNumerico(tokens[tokens.length - 1])) return null;

  const cantidad = cantidadDeLaFila(tokens);
  if (!cantidad || cantidad.indice < 1) return null;

  const lote = tokens[cantidad.indice - 1];
  if (!esLote(lote)) return null;

  const descripcion = limpiarDescripcion(tokens.slice(0, cantidad.indice - 1));
  if (contarLetras(descripcion) < 6) return null;

  return { descripcion, lote, cantidad: cantidad.valor };
}

export function parsearLineasFacturaGenerica(allLines) {
  const productsByDesc = new Map();
  let pendiente = null;
  let antiguedad = 0;

  for (const rawLine of allLines || []) {
    const linea = String(rawLine ?? "").replace(/\s+/g, " ").trim();
    if (!linea || esRuido(linea)) continue;

    const fecha = buscarFechaCaducidad(linea);

    if (fecha) {
      const partida = analizarFilaConFecha(linea, fecha);
      if (partida) {
        acumularProducto(productsByDesc, { ...partida, caducidad: fecha.fecha });
        pendiente = null;
        continue;
      }

      // Fila que sólo trae la caducidad: es la continuación de la partida anterior. Se
      // exige que hable de caducidad (o que sea prácticamente sólo la fecha) porque si no,
      // la fecha de vencimiento de la factura se le colgaba al último renglón leído.
      if (pendiente && (mencionaCaducidad(linea) || contarLetras(linea) <= 3)) {
        acumularProducto(productsByDesc, { ...pendiente, caducidad: fecha.fecha });
        pendiente = null;
        continue;
      }
    } else {
      const partida = analizarFilaSinFecha(linea);
      if (partida) {
        pendiente = partida;
        antiguedad = 0;
        continue;
      }
    }

    // Nadie consumió la partida pendiente: envejece y se descarta
    if (pendiente && ++antiguedad > VIGENCIA_PENDIENTE) pendiente = null;
  }

  return productsByDesc;
}

export default parsearLineasFacturaGenerica;
